import qs from "qs";

import { LOCAL_DOMAIN } from "@/constants/routes";
import type { HttpRequest, HttpResponse } from "./types";

const transformObjectKeysToLowercase = (
  obj: Record<string, string> | undefined
): Record<string, string> => {
  const hasValidObject =
    obj && typeof obj === "object" && Object.keys(obj).length > 0;

  const entriesWithLowercaseKeys = hasValidObject
    ? Object.entries(obj).map(([key, value]) => [key.toLowerCase(), value])
    : [];

  return Object.fromEntries(entriesWithLowercaseKeys);
};

/**
 * makeRequest
 *
 * Builds a fully decorated URL from the request template and fetches the
 * resource, resolving the parsed body paired with the raw response. See
 * https://developer.mozilla.org/en-US/docs/Web/API/Response.
 * @param baseURL - The base URL to prepend to the request path.
 * @param request - The request a request template describes.
 * @returns Resolves to { data, headers } where data is the parsed JSON body
 * (or {}).
 */
export const makeRequest = (
  baseURL: string,
  { HTTPMethod: method, path, queryParams, headers, body: data }: HttpRequest
): Promise<HttpResponse> => {
  // Create a fully decorated URL pointing to the desired resource to fetch.
  const params =
    qs.stringify(queryParams || {}, {
      addQueryPrefix: true,
      arrayFormat: "brackets",
      sort: (a, b) => a.localeCompare(b),
    }) || "";

  const url = `${baseURL}${path}${params}`;

  // Header args are capitalized, but we need to check them and fetch does not care about case.
  const existingHeaders = transformObjectKeysToLowercase(headers);
  const newHeaders: Record<string, string> = {};

  // Build up the request options that instruct fetch how to fulfill this request.
  const options: RequestInit = {};

  if (!existingHeaders.accept) {
    newHeaders.accept = "application/json, text/plain, */*";
  }

  if (data && method.toLowerCase() !== "get") {
    newHeaders["content-type"] = "application/json";
    options.body = JSON.stringify(data);
  }

  // Enforces this as a CORS request when working locally.
  if (window.location.hostname.includes(LOCAL_DOMAIN)) {
    options.mode = "cors";
  }

  options.headers = {
    ...existingHeaders,
    ...newHeaders,
  };

  // The method MUST be in caps or certain requests will fail.
  options.method = ("" + method).toUpperCase();

  let responseHeader: Response;

  return fetch(url, options)
    .then((response) => {
      // UMS requests fire and fail when the app first starts. RevM will stop processing
      // subsequent requests if the response is returned when NOT in an "ok" status.
      responseHeader = response;
      const contentType = response.headers.get("content-type");

      // Return a promise of a json body only if the body content type is json.
      if (contentType && contentType.includes("application/json")) {
        return response.json();
      }

      // Otherwise return an empty body
      return Promise.resolve({});
    })
    .then((json) => ({ data: json, headers: responseHeader })); // The deserializer code expects this data shape.
};
